import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { api, apiError } from "@/lib/api";

export function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.next !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      localStorage.setItem("sm_token", data.token);
      toast.success(data.message);
      setOpen(false);
      setF({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="change-password-btn"
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50">
        <KeyRound className="h-[18px] w-[18px]" /> Change password
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Change password</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <PasswordInput required placeholder="Current password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="cp-current" />
            <PasswordInput required placeholder="New password (8+ characters)" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} data-testid="cp-new" />
            <PasswordInput required placeholder="Confirm new password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="cp-confirm" />
            <p className="text-xs text-slate-400">You'll stay signed in here; all other devices will be signed out.</p>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="cp-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SetPasswordDialog({ title, description, onSubmit, triggerLabel = "Reset password", testId, inline = false }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try { await onSubmit(pw); setOpen(false); setPw(""); } finally { setBusy(false); }
  };
  if (inline) return (
    <div className="w-full">
      {!open ? (
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={testId}>{triggerLabel}</Button>
      ) : (
        <form onSubmit={submit} className="mt-2 space-y-2 sm-fade-up" data-testid={`${testId}-dialog`}>
          <p className="text-xs text-slate-500">{description}</p>
          <div className="flex gap-2">
            <PasswordInput required autoFocus placeholder="New temporary password (8+)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid={`${testId}-input`} />
            <Button type="submit" size="sm" disabled={busy} className="h-9 bg-emerald-500 hover:bg-emerald-600" data-testid={`${testId}-submit`}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Set
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-9" onClick={() => { setOpen(false); setPw(""); }} data-testid={`${testId}-cancel`}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={testId}>{triggerLabel}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-slate-500">{description}</p>
            <PasswordInput required placeholder="New temporary password (8+)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid={`${testId}-input`} />
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`${testId}-submit`}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Set password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
